import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createAchievement, deleteAchievement, getAchievement, getAchievements, getSuggestions, resetAiAnalysis, updateAchievement } from '@/api/achievements'
import type { Achievement } from '@/types'

type Filters = {
  date_from?: string
  date_to?: string
  rating?: number[]
  ai_rating?: number[]
  ordering?: string
  search?: string
}

export function useAchievements(filters: Filters = {}) {
  return useQuery({
    queryKey: ['achievements', filters],
    queryFn: () => getAchievements(filters),
  })
}

export function useAchievement(id: number) {
  return useQuery({
    queryKey: ['achievements', id],
    queryFn: () => getAchievement(id),
  })
}

export function useCreateAchievement() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: Partial<Achievement>) => createAchievement(data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['achievements'] }),
  })
}

export function useUpdateAchievement(id: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: Parameters<typeof updateAchievement>[1]) => updateAchievement(id, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['achievements'] })
      qc.invalidateQueries({ queryKey: ['achievements', id] })
    },
  })
}

export function useDeleteAchievement() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => deleteAchievement(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['achievements'] }),
  })
}

export function useSuggestions(q: string) {
  return useQuery({
    queryKey: ['suggestions', q],
    queryFn: () => getSuggestions(q),
    enabled: q.length >= 1,
  })
}

export function useResetAiAnalysis() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => resetAiAnalysis(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['achievements'] }),
  })
}

export function useBulkResetAiAnalysis() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (ids: number[]) => Promise.all(ids.map((id) => resetAiAnalysis(id))),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['achievements'] }),
  })
}
